import FF from "@/components/forms/FormField.module.css";
import TF from "@/components/forms/TextField.module.css";

import { HeroBlock } from "@/components/HeroBlock.tsx";
import { NoticeBlock } from "@/components/NoticeBlock.tsx";
import { Separator } from "@/components/Separator.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { CreateFormAction } from "@/components/forms/CreateFormAction.tsx";
import { FormDebugger } from "@/components/forms/FormDebugger.tsx";
import { FormField } from "@/components/forms/FormField.tsx";
import { IconRadio } from "@/components/forms/IconRadio.tsx";
import { MonthlyCostField } from "@/components/forms/MonthlyCostField.tsx";
import RA from "@/components/forms/Radio.module.css";
import { RadioGroupDiv } from "@/components/forms/RadioGroupDiv.tsx";
import R from "@/components/forms/Radiogroup.module.css";
import { WCheckbox } from "@/components/forms/WCheckbox.tsx";
import { WHookForm } from "@/components/forms/WHookForm.tsx";
import { WRadioGroup } from "@/components/forms/WRadioGroup.tsx";
import { CurrencyHourly, CurrencyMonthly } from "@/components/l10n/Currency.tsx";
import { CreateAnother, LoadingProgress } from "@/components/loading/Loading.tsx";
import { showModal } from "@/components/modal/registerModal.tsx";
import { useConfig } from "@/config.ts";
import { getResourceDescription } from "@/modules/api/resourceTypes.tsx";
import { useStandardMutation, useStandardSuspenseQuery } from "@/modules/api/useStandardMutation.ts";
import { BillingDiscountNotice } from "@/modules/billing/BillingDiscountNotice.tsx";
import { BillingAccountField } from "@/modules/billing/forms/BillingAccountField.tsx";
import { AddSshKeyModalContent } from "@/modules/compute/AddSshKeyModal.tsx";
import { VmCloneHeaderBlock } from "@/modules/compute/ModalHeaderBlock.tsx";
import { ParamsTextField } from "@/modules/compute/ParamsTextField.tsx";
import { PasswordFields } from "@/modules/compute/PasswordFields.tsx";
import { VmRootUserWarning } from "@/modules/compute/VmRootUserWarning.tsx";
import { type VmPriceFieldsOmitSize, VmSizeSelect } from "@/modules/compute/VmSizeSelect.tsx";
import { reinstallTypes, t_uefiWarning } from "@/modules/compute/labels.tsx";
import { vmLink } from "@/modules/compute/links.ts";
import { OsVersionSelectCustom } from "@/modules/compute/os/OsVersionSelect.tsx";
import { type ComputeImageType, getResourceTypeImages, osIsWindows } from "@/modules/compute/os/os.ts";
import { getPoolsForLocation } from "@/modules/compute/pool/pool.ts";
import { sshKeysQuery } from "@/modules/compute/sshKeysQuery.ts";
import { type ItemType, type VmCreateInputs, VmCreateViewModel } from "@/modules/compute/vmCreateViewModel.tsx";
import { availableImagesAtom } from "@/modules/compute/vmImagesQuery.ts";
import { usePackages } from "@/modules/compute/vmPackages.ts";
import { parametersByNameAtom } from "@/modules/compute/vmParametersQuery.ts";
import { VM_CREATE_TIMER, vmCreateMutation } from "@/modules/compute/vmQuery.ts";
import { sizeToVmPriceFields } from "@/modules/compute/vmSizeSelectUtils.ts";
import { LocationFieldCustom } from "@/modules/location/LocationField.tsx";
import { useLocationsForType } from "@/modules/location/locationQuery.ts";
import { CreateAndAssignIpField } from "@/modules/network/CreateAndAssignIpField.tsx";
import { VpcNetworkSelect } from "@/modules/network/VpcNetworkSelect.tsx";
import { canCreateResourceHostPoolsAtom } from "@/modules/pools/hostPoolsQuery.ts";
import { PricingModal } from "@/modules/pricing/PricingModal.tsx";
import { pricesAtom } from "@/modules/pricing/pricingQuery.ts";
import { getVmCreatePrice, getWindowsLicensePrice, type ResourcePrices } from "@/modules/pricing/resourcePricing.ts";
import { cn } from "@/utils/classNames.ts";
import { castSetter } from "@/utils/getField.tsx";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { useOnce } from "@/utils/react/useOnce.ts";
import { useGenerateUuid } from "@/utils/useGenerateUuid.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { Link, Navigate } from "@tanstack/react-router";
import type { VmCreateBody, VmParameter } from "@warrenio/api-spec/spec.oats.gen";
import { filterObjNulls } from "@warrenio/utils/collections/filterNulls";
import { useEffect, useMemo, useState } from "react";
import { CheckboxGroup, Input, Radio, RadioGroup } from "react-aria-components";
import { Controller, type SubmitHandler, useForm, useFormContext } from "react-hook-form";

const types: ItemType[] = [
    ...reinstallTypes,
    {
        id: "empty_disk",
        title: <Trans>Empty disk (for ISO booting)</Trans>,
        icon: "jp-disc-icon",
        description: (
            <Trans>
                Use the ISO boot option after the virtual machine has been created to install an operating system
                yourself.
            </Trans>
        ),
    },
];

interface VmCreateProps {
    inputs?: Partial<VmCreateInputs>;
    /** Used to dismiss toasts of the previous (failed) request */
    previousRequestUuid?: string;
    onChangeInputs?: (inputs: VmCreateInputs) => void;
}

function getUsernameForWindows() {
    return "administrator";
}

export function VmCreate(props: VmCreateProps) {
    return import.meta.env.DEV ? <VmCreateDebugger {...props} /> : <VmCreateForm {...props} />;
}

const description_user = (
    <Trans>
        A user account will be created with this username. It can be used to log in to the VM. Can contain ASCII
        letters, digits, underscores and hyphens. Must start with a letter or underscore. Maximum length is 25
        characters.
    </Trans>
);

const description_vmName = (
    <Trans>Informative name. It will also be used to generate a suitable hostname for the VM (if applicable).</Trans>
);

function VmCreateForm({ inputs, previousRequestUuid, onChangeInputs }: VmCreateProps) {
    const isFromSnapshot = !!inputs?.source_replica;

    //#region Hooks
    const { t } = useLingui();
    const params = useSuspenseQueryAtom(parametersByNameAtom);
    const pools = useSuspenseQueryAtom(canCreateResourceHostPoolsAtom);
    const priceList = useSuspenseQueryAtom(pricesAtom);

    const allImages = useSuspenseQueryAtom(availableImagesAtom);

    const allSizePackages = usePackages();

    const { vmAppsEnabled } = useConfig();
    const availableTypes = vmAppsEnabled ? types : types.filter((i) => i.id !== "app_catalog");

    const [cloudInit, setCloudInit] = useState<boolean>(!!inputs?.cloud_init);

    function getImages(type: ComputeImageType) {
        return getResourceTypeImages(allImages, type);
    }

    const mutation = useStandardMutation(vmCreateMutation);

    const locations = useLocationsForType("virtual_machine");

    const requestUuid = useGenerateUuid(undefined);

    const viewModel = useOnce(() => {
        return new VmCreateViewModel(availableTypes, allImages, locations, pools, allSizePackages, inputs);
    });

    const defaultValues = useMemo(() => viewModel.getDefaultValues(), [viewModel]);

    /// Form
    const form = useForm<VmCreateInputs>({
        disabled: mutation.isPending,

        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues,
    });

    const { watch, control, setValue, getValues } = form;

    viewModel.attachGetterAndSetter(getValues, castSetter(setValue));

    useEffect(() => {
        const subscription = watch((_value, _info) => {
            onChangeInputs?.(getValues()); // for form debugger
        });
        return () => subscription.unsubscribe();
    }, [watch, getValues, onChangeInputs]);

    //#endregion Hooks

    if (mutation.isSuccess) {
        // We can navigate to the VM view (even though the invalidated list query has not necessarily finished yet)
        // because of the optimistic update in the mutation's `onSuccess` handler.
        return <Navigate {...vmLink(mutation.data)} />;
    }

    const usernameParams = params.get("username")!;

    /** Snapshots and clones should not send and display credentials */
    const hasOs = watch("typeId") !== "empty_disk";
    const hasCredentials = !isFromSnapshot && hasOs;
    const canChooseBootFirmware = isFromSnapshot || !hasOs;

    const onSubmit: SubmitHandler<VmCreateInputs> = async (inputs) => {
        // Remove irrelevant fields by destructuring to unused variables (`_1`)
        const {
            size: { isCustom: _1, ...size },
            typeId: _2,
            size_ranges: _3,
            location,
            os,
            username,
            public_keys,
            password,
            password_confirm: _4,
            ...body
        } = inputs;

        const credentials: Pick<VmCreateBody, "username" | "password" | "public_keys"> =
            // Backwards compatibility check: new backend makes username not mandatory
            !hasCredentials && !usernameParams.mandatory
                ? {}
                : {
                      // "_dummy" is for backwards compatibility for snapshots
                      username: osIsWindows(os) ? getUsernameForWindows() : (username ?? "_dummy"),
                      password: password ?? "_dummy",
                      public_keys,
                  };

        await mutation.mutateAsync({
            requestUuid,
            previousRequestUuid,
            inputs,
            // XXX: Need to remove nulls or backend breaks due to hug issues (WAR-5843)
            body: filterObjNulls({
                description: body.name.trim(), // Currently unused
                ...body,
                ...os,
                ...size,
                ...credentials,
            }),
            location,
        });
    };

    const nameParams = params.get("name")!;
    const cloudInitParams: VmParameter = {
        constraint: "regexp",
        description: "",
        expression: "",
        mandatory: false,
        parameter: "cloud_init",
        type: "string",
    };

    const isWindows = osIsWindows(watch("os"));

    const locationPools = getPoolsForLocation(pools, watch("location")).filter((l) => l.is_visible);

    const createFields = {
        ...watch("os"),
        location: watch("location"),
        designated_pool_uuid: watch("designated_pool_uuid"),
    } satisfies VmPriceFieldsOmitSize;

    const licenseType = getImages(watch("typeId")).find((item) => item.os_name === watch("os").os_name)?.license_type;

    return (
        <WHookForm
            form={form}
            onSubmit={onSubmit}
            loader={
                mutation.isPending && (
                    <LoadingProgress name={form.getValues().name} type="virtual_machine" lifeTime={VM_CREATE_TIMER}>
                        <CreateAnother type="virtual_machine" />
                    </LoadingProgress>
                )
            }
        >
            <CreateHero />

            {!isFromSnapshot ? (
                <>
                    <Controller
                        control={control}
                        name="typeId"
                        render={({ field }) => (
                            <WRadioGroup
                                wide
                                isDisabled={isFromSnapshot}
                                label={t`Type`}
                                isRequired
                                value={field.value}
                                onChange={viewModel.onChangeType}
                                description={availableTypes.find((i) => i.id === field.value)?.description}
                            >
                                {availableTypes.map((item) => (
                                    <IconRadio key={item.id} {...item} />
                                ))}
                            </WRadioGroup>
                        )}
                    />

                    <Separator />

                    {hasOs && (
                        <FormField wide label={t`OS`} isRequired>
                            <RadioGroupDiv>
                                <Controller
                                    control={control}
                                    name="os"
                                    render={({ field }) => (
                                        <OsVersionSelectCustom
                                            items={getImages(watch("typeId"))}
                                            value={field.value}
                                            onChange={viewModel.onChangeOs}
                                        />
                                    )}
                                />
                            </RadioGroupDiv>

                            <WindowsLicenseCopy
                                createFields={{
                                    ...sizeToVmPriceFields(watch("size"), true),
                                    ...createFields,
                                    license_type: licenseType,
                                }}
                                priceList={priceList}
                            />
                        </FormField>
                    )}

                    {locations.showLocations && (
                        <Controller
                            control={control}
                            name="location"
                            render={({ field }) => (
                                <LocationFieldCustom
                                    value={field.value}
                                    onChange={viewModel.onLocationChange}
                                    locations={locations}
                                />
                            )}
                        />
                    )}
                </>
            ) : (
                <FormField wide label={t`From`} isRequired>
                    <div className="pb-2">
                        <VmCloneHeaderBlock inputs={inputs} />
                    </div>

                    <NoticeBlock icon="jp-warning-icon">
                        <b className="uppercase">
                            <Trans>Warning</Trans>
                        </b>
                        <br />
                        <Trans>
                            Only the boot disk will be created! This may cause the OS to not boot properly if the
                            configuration in the OS of source snapshot requires additional disks to be present.
                        </Trans>
                    </NoticeBlock>
                </FormField>
            )}

            <Separator />

            {!isFromSnapshot && locationPools.length ? (
                <Controller
                    control={control}
                    name="designated_pool_uuid"
                    render={({ field }) => (
                        <WRadioGroup
                            wide
                            label={t`Server class`}
                            isRequired
                            value={field.value}
                            onChange={viewModel.onDesignatedPoolChange}
                        >
                            {locationPools.map((item) => (
                                <IconRadio
                                    wide
                                    icon="jp-cluster-icon"
                                    id={item.uuid}
                                    title={item.name}
                                    key={item.uuid}
                                    desc={item.description}
                                    {...item}
                                />
                            ))}
                        </WRadioGroup>
                    )}
                />
            ) : null}

            <FormField wide label={t`Size`} isRequired>
                <RadioGroupDiv>
                    <Controller
                        control={control}
                        name="size"
                        render={() => (
                            <VmSizeSelect
                                vmData={createFields}
                                value={watch("size")}
                                sliderRanges={watch("size_ranges")}
                                onChange={viewModel.onSizeChange}
                                sizeParams={viewModel.getSizeParams()}
                            />
                        )}
                    />
                </RadioGroupDiv>

                {!!watch("billing_account_id") && (
                    <BillingDiscountNotice billingAccountId={watch("billing_account_id")!} />
                )}
            </FormField>

            <Separator />

            <CreateAndAssignIpField<VmCreateInputs>
                name="reserve_public_ip"
                control={control}
                location={watch("location")}
            />

            <FormField
                isRequired
                label={t`VPC network`}
                description={t`Choose a VPC network for your resource from already existing one or create a new private network.`}
            >
                <Controller
                    control={control}
                    name="network_uuid"
                    render={({ field }) => (
                        <VpcNetworkSelect
                            location={watch("location")}
                            isDisabled={field.disabled}
                            valueKey={field.value}
                            onChange={(vpc) => field.onChange(vpc.uuid)}
                        />
                    )}
                />
            </FormField>

            <Separator />

            {!hasCredentials ? null : isWindows ? (
                <FormField isDisabled label={t`Username`}>
                    <Input
                        readOnly
                        value={isFromSnapshot ? inputs?.username : getUsernameForWindows()}
                        className={cn(FF.FormFieldInput, TF.Input)}
                    />
                </FormField>
            ) : (
                <ParamsTextField
                    form={form}
                    field="username"
                    params={{ ...usernameParams, mandatory: true }}
                    autoComplete="username"
                    label={t`Username`}
                    description={description_user}
                    isReadOnly={isWindows}
                >
                    <VmRootUserWarning />
                </ParamsTextField>
            )}

            {hasCredentials && <PasswordFields />}

            {hasCredentials && !isWindows && <SshKeyField />}

            {!isWindows && (
                <>
                    <FormField label={t`Initialization script`}>
                        <WCheckbox isSelected={cloudInit} onChange={setCloudInit}>
                            <Trans>Add custom configuration for the virtual machine</Trans>
                        </WCheckbox>
                    </FormField>

                    {cloudInit && (
                        <ParamsTextField
                            form={form}
                            field="cloud_init"
                            params={cloudInitParams}
                            multilineRows={5}
                            // Empty label to align with other fields
                            label=" "
                            description={t`Provide a valid cloud-config as JSON or YAML. This configuration will be passed as cloud-init user-data to the virtual machine. See cloud-init documentation about cloud-config for all the available options.`}
                            isMonospace
                        />
                    )}
                </>
            )}

            {canChooseBootFirmware && (
                <FormField
                    label={<Trans comment="Virtual machine choice: UEFI or BIOS">Boot mode</Trans>}
                    description={
                        <>
                            <Trans>Choose UEFI if the VM you are importing requires it.</Trans>
                            <br />
                            {t_uefiWarning}
                        </>
                    }
                >
                    <Controller
                        control={control}
                        name="boot_firmware"
                        render={({ field }) => (
                            <RadioGroup
                                className={R.RadioGroup}
                                aria-label="Boot mode"
                                value={field.value}
                                orientation="horizontal"
                                name="boot_firmware"
                                onChange={field.onChange}
                            >
                                <Radio className={RA.Radio} value="bios">
                                    BIOS
                                </Radio>
                                <Radio className={RA.Radio} value="uefi">
                                    UEFI
                                </Radio>
                            </RadioGroup>
                        )}
                    />
                </FormField>
            )}

            <ParamsTextField
                form={form}
                field="name"
                params={nameParams}
                label={t`Resource name`}
                description={description_vmName}
            />

            <Separator />

            <VmMonthlyCostField
                createFields={{
                    ...sizeToVmPriceFields(watch("size"), true),
                    ...createFields,
                    license_type: licenseType,
                }}
                billingAccountId={watch("billing_account_id")}
                priceList={priceList}
            />

            <BillingAccountField resourceType="virtual_machine" isRequired />

            <Separator />

            <CreateFormAction resourceType="virtual_machine" />
        </WHookForm>
    );
}

function WindowsLicenseCopy({
    createFields,
    priceList,
}: {
    createFields: VmPriceFieldsOmitSize;
    priceList: ResourcePrices;
}) {
    //#region Hooks
    const { watch } = useFormContext<VmCreateInputs>();
    //#endregion

    if (createFields.license_type !== "WINDOWS") return null;

    const price = getWindowsLicensePrice(priceList, {
        ...createFields,
        ...sizeToVmPriceFields(watch("size"), true),
    });

    return (
        !!price?.hourly && (
            <p className="pt-2 text-muted">
                <Trans>
                    This distribution adds a <CurrencyHourly value={price.hourly} /> cost per hour for licensing (
                    <CurrencyMonthly value={price.hourly} /> per month).
                </Trans>{" "}
                <PricingModal>
                    <Trans>Price list</Trans>
                </PricingModal>
            </p>
        )
    );
}

function VmMonthlyCostField({
    createFields,
    billingAccountId,
    priceList,
}: {
    createFields: VmPriceFieldsOmitSize;
    billingAccountId?: number;
    priceList: ResourcePrices;
}) {
    //#region Hooks
    const { watch } = useFormContext<VmCreateInputs>();
    //#endregion

    const price = getVmCreatePrice(
        priceList,
        { ...createFields, ...sizeToVmPriceFields(watch("size"), true) },
        watch("reserve_public_ip"),
    );

    return (
        <MonthlyCostField
            price={price}
            defaultLocation={createFields.location}
            defaultHostPool={createFields.designated_pool_uuid}
            billingAccountId={billingAccountId}
        />
    );
}

function CreateHero() {
    const { t } = useLingui();
    return (
        <HeroBlock title={t`Create new`} icon="jp-compute-cloud-icon">
            {getResourceDescription("virtual_machine")}
        </HeroBlock>
    );
}

/** Debugger to check if re-loading the form works (this is used to eg. retry creating a VM from the error notification) */
function VmCreateDebugger({ inputs, onChangeInputs, ...props }: VmCreateProps) {
    return (
        <FormDebugger inputs={inputs} onChangeInputs={onChangeInputs}>
            {({ key, inputs, onChangeInputs }) => (
                <VmCreateForm key={key} {...props} inputs={inputs} onChangeInputs={onChangeInputs} />
            )}
        </FormDebugger>
    );
}

function SshKeyField() {
    const { t } = useLingui();
    const { getValues, setValue, control } = useFormContext<VmCreateInputs>();
    const { data } = useStandardSuspenseQuery(sshKeysQuery);

    function onKeyAdded(value: string) {
        setValue("public_keys", [...getValues("public_keys"), value]);
    }

    return (
        <FormField
            label={t`SSH keys`}
            description={
                <Trans>
                    SSH keys can be managed under your{" "}
                    <Link to="/profile" target="_blank" className="text-primary">
                        user profile
                    </Link>
                    .
                </Trans>
            }
        >
            <Controller
                control={control}
                name="public_keys"
                render={({ field }) => (
                    <CheckboxGroup
                        aria-label="SSH keys"
                        className="flex flex-wrap gap-4 pb-3"
                        value={field.value ?? null}
                        onChange={(value) => field.onChange(value)}
                    >
                        {[...data.values()].map((item) => (
                            <WCheckbox key={item.uuid} value={item.public_key}>
                                {item.name}
                            </WCheckbox>
                        ))}
                    </CheckboxGroup>
                )}
            />

            <WButton
                variant="border"
                action={() => showModal(<AddSshKeyModalContent onKeyAdded={onKeyAdded} />)}
                color="primary"
                label={t`Add SSH key`}
                icon="jp-icon-add"
            />
        </FormField>
    );
}
